import type { GroupPublic } from '@repo/types/groups';
import type { ChangeEvent } from 'react';

import { useGetGroupsInfinite } from '@repo/api/group/useGetGroupsInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { useState } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export type PostAudienceValue = 'public' | 'groups';
export type PostAudienceTab = 'main' | 'audience';

export function usePostDialog(global: boolean) {
  // Tab state
  const [tabs, setTabs] = useState<PostAudienceTab>('main');

  // Audience state
  const [searchGroupValue, setSearchGroupValue] = useState<string>('');
  const [selectedGroups, setSelectedGroups] = useState<GroupPublic[]>([]);

  // File state
  const [files, setFiles] = useState<{ id: string; file: File }[]>([]);

  // Derived states
  const audience: PostAudienceValue = global ? 'public' : 'groups';
  const searchGroupDebouncedValue = useDebounceValue(searchGroupValue, 400);
  const isMainTab: boolean = tabs === 'main';
  const isAudienceTab: boolean = tabs === 'audience';
  const shouldEnableGroupsQuery: boolean = isAudienceTab && audience === 'groups';

  // Groups query
  const { errorMessage: groupsErrorMessage, ...getGroupsInfinite } = useGetGroupsInfinite(searchGroupDebouncedValue, shouldEnableGroupsQuery);

  const { data: groups, isEmpty: groupsIsEmpty, noResults: groupsNoResults } = useInfiniteQueryResult(getGroupsInfinite.data, searchGroupDebouncedValue);

  // Tab handlers
  const onTabsValueChange = (tab: string) => {
    setTabs(tab as PostAudienceTab);
  };

  // Audience handlers
  const onRemoveGroup = (id: number) => {
    setSelectedGroups((curr) => curr.filter((group) => group.id !== id));
  };

  // File handlers
  const onAddFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const newFiles = Array.from(e.target.files).map((file) => {
      if (file.size > 500 * 1024 * 1024) {
        toast.error('Błąd', {
          description: 'Maksymalny rozmiar pliku to 500MB',
        });
      }
      return {
        id: uuidv4(),
        file,
      };
    });
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    return e.target.files;
  };

  const onRemoveFile = (id: string | number) => {
    setFiles((curr) => curr.filter((file) => file.id !== id));
  };

  // Reset function - resets all dialog state
  const resetDialog = () => {
    setTabs('main');
    setSearchGroupValue('');
    setSelectedGroups([]);
    setFiles([]);
  };

  return {
    // Tab state
    tabs,
    setTabs,
    isMainTab,
    isAudienceTab,
    onTabsValueChange,

    // Audience state
    audience,
    searchGroupValue,
    setSearchGroupValue,
    selectedGroups,
    setSelectedGroups,
    onRemoveGroup,

    // File state and handlers
    files,
    setFiles,
    onAddFiles,
    onRemoveFile,

    // Groups query
    groups,
    groupsIsEmpty,
    groupsNoResults,
    groupsErrorMessage,
    getGroupsInfinite,

    // Reset functions
    resetDialog,
  };
}
